import { Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ControlValueAccessor, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AbstractFormControlComponent } from '../abstract-form-control.component';
import { OtpFormControl } from './otp-form-control';

@Component({
  standalone: false,
  selector: 'app-otp-form-control',
  templateUrl: './otp-form-control.component.html',
  styleUrls: ['./otp-form-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OtpFormControlComponent),
      multi: true
    }
  ]
})
export class OtpFormControlComponent
  extends AbstractFormControlComponent<OtpFormControl>
  implements ControlValueAccessor, OnDestroy, OnInit
{
  @Input() override parentForm: FormGroup = new FormGroup({});
  @Input() override formControlName: string = '';
  @Input() override title: string = '';
  @Input() forceInvalid = false;
  @Input() externalErrorMessage: string | null = null;

  @ViewChildren('otpInput') private otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  digits: string[] = [];
  private valueChangesSubscription?: Subscription;

  onChange: (value: string | null) => void = () => {};
  onTouched: () => void = () => {};

  ngOnInit(): void {
    this.syncDigits(this.normalizeValue(this.control.value));

    this.valueChangesSubscription = this.control.valueChanges.subscribe(value => {
      const normalizedValue = this.normalizeValue(value);

      if (normalizedValue !== this.digits.join('')) {
        this.syncDigits(normalizedValue);
      }
    });
  }

  ngOnDestroy(): void {
    this.valueChangesSubscription?.unsubscribe();
  }

  writeValue(value: string | null): void {
    this.syncDigits(this.normalizeValue(value));
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({ emitEvent: false });
      return;
    }

    this.control.enable({ emitEvent: false });
  }

  onDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsOnly = input.value.replace(/\D/g, '');

    if (!digitsOnly) {
      this.digits[index] = '';
      this.updateControlValue();
      return;
    }

    if (digitsOnly.length >= this.length) {
      this.replaceAllDigits(digitsOnly);
      this.updateControlValue();
      this.focusInput(this.length - 1);
      return;
    }

    this.fillDigits(index, digitsOnly);
    this.updateControlValue();

    const nextIndex = Math.min(index + digitsOnly.length, this.length - 1);
    this.focusInput(nextIndex);
  }

  onKeyDown(index: number, event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();

      if (this.digits[index]) {
        this.digits[index] = '';
        this.updateControlValue();
        return;
      }

      if (index > 0) {
        this.digits[index - 1] = '';
        this.updateControlValue();
        this.focusInput(index - 1);
      }

      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      this.digits[index] = '';
      this.updateControlValue();
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusInput(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < this.length - 1) {
      event.preventDefault();
      this.focusInput(index + 1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      this.focusInput(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      this.focusInput(this.length - 1);
      return;
    }

    if (event.key.length === 1 && !/\d/.test(event.key)) {
      event.preventDefault();
    }
  }

  onPaste(index: number, event: ClipboardEvent): void {
    const pastedValue = event.clipboardData?.getData('text')?.replace(/\D/g, '') ?? '';

    if (!pastedValue) {
      return;
    }

    event.preventDefault();

    if (pastedValue.length >= this.length) {
      this.replaceAllDigits(pastedValue);
      this.updateControlValue();
      this.focusInput(this.length - 1);
      return;
    }

    this.fillDigits(index, pastedValue);
    this.updateControlValue();
    this.focusInput(Math.min(index + pastedValue.length, this.length - 1));
  }

  onFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;

    input.select();
  }

  onBlur(): void {
    this.onTouched();
    this.control.markAsTouched();
  }

  get length(): number {
    return this.control?.length ?? 6;
  }

  get showErrorState(): boolean {
    return this.forceInvalid || (this.control.invalid && this.control.touched);
  }

  get showBuiltInErrors(): boolean {
    return !this.externalErrorMessage && !this.control.hideErrorMessages && this.control.touched && !!this.control.errors;
  }

  private fillDigits(startIndex: number, value: string): void {
    const limitedValue = value.slice(0, this.length - startIndex);

    limitedValue.split('').forEach((digit, offset) => {
      this.digits[startIndex + offset] = digit;
    });
  }

  private replaceAllDigits(value: string): void {
    this.syncDigits(this.normalizeValue(value));
  }

  private updateControlValue(): void {
    const joinedValue = this.digits.join('');
    const nextValue = joinedValue.length ? joinedValue : null;

    this.onChange(nextValue);
    this.control.setValue(nextValue, { emitEvent: false });
    this.control.updateValueAndValidity({ emitEvent: false });
    this.control.markAsDirty();
  }

  private syncDigits(value: string): void {
    this.digits = Array.from({ length: this.length }, (_, index) => value[index] ?? '');
  }

  private normalizeValue(value: unknown): string {
    return String(value ?? '')
      .replace(/\D/g, '')
      .slice(0, this.length);
  }

  private focusInput(index: number): void {
    const input = this.otpInputs.get(index)?.nativeElement;

    input?.focus();
    input?.select();
  }
}